import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { OnboardingSession } from '../../types/onboarding.types';
import { apiService } from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import { useAppSelector } from '../../redux/hooks';

// Session Card component
const OnboardingSessionCard: React.FC<{ session: OnboardingSession }> = ({ session }) => {
  const getStatusColor = (completed: boolean): 'success' | 'warning' => {
    return completed ? 'success' : 'warning';
  };

  const getStatusIcon = (completed: boolean) => {
    return completed ? <CheckCircleIcon color="success" sx={{ fontSize: 20 }} /> : <ScheduleIcon color="warning" sx={{ fontSize: 20 }} />;
  };

  const getStatusText = (completed: boolean) => {
    return completed ? 'Completed' : 'Pending';
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssignmentIcon color="primary" />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {session.topic}
          </Typography>
          <Chip
            icon={getStatusIcon(session.sessionCompleted)}
            label={getStatusText(session.sessionCompleted)}
            color={getStatusColor(session.sessionCompleted)}
            variant="outlined"
            size="small"
          />
        </Box>

        {session.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {session.description}
          </Typography>
        )}

        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 16 }} color="action" />
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(session.startDateTime)} - {formatDateTime(session.endDateTime)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ fontSize: 16 }} color="action" />
            <Typography variant="body2" color="text.secondary">
              Presenter: {session.presenter}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Duration: {session.duration} hours
            </Typography>
            {session.confidenceLevel && (
              <Typography variant="body2" color="text.secondary">
                Confidence: {session.confidenceLevel}/10
              </Typography>
            )}
          </Box>

          {session.resourceLinks && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Resources: {session.resourceLinks}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const Onboarding: React.FC = () => {
  const [sessions, setSessions] = useState<OnboardingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    loadMySessions();
  }, []);

  const loadMySessions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getMyOnboardingSessions();
      setSessions(data);
      setError(null);
    } catch (err) {
      console.error('Error loading my onboarding sessions:', err);
      setError('Failed to load your onboarding sessions');
    } finally {
      setLoading(false);
    }
  };

  const completedSessions = sessions.filter(s => s.sessionCompleted).length;
  const totalSessions = sessions.length;
  const completionPercentage = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="My Onboarding"
        subtitle={`Welcome ${user?.firstName}! Track your onboarding progress and complete your assigned sessions.`}
      />

      {/* Progress Overview */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Your Progress
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Overall Completion
              </Typography>
              <Typography variant="h4" color="primary">
                {completionPercentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{ height: 10, borderRadius: 5, mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              {completedSessions} of {totalSessions} sessions completed
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 2 
        }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{completedSessions}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Sessions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{totalSessions - completedSessions}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Sessions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Sessions List */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Your Onboarding Sessions
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {sessions.length === 0 ? (
          <Alert severity="info">
            No onboarding sessions have been assigned to you yet. Please contact your manager or HR department.
          </Alert>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              md: 'repeat(2, 1fr)' 
            }, 
            gap: 2 
          }}>
            {sessions.map((session) => (
              <OnboardingSessionCard 
                key={session.id} 
                session={session} 
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Onboarding;
